import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import { encodePathSegment } from '../../lib/url';
import { describeTxError } from '../../lib/txError';
import Modal from '../../components/Modal';
import { useWeb3 } from '../../contexts/Web3Context';
import { type ChainInfo, useNetwork } from '../../contexts/NetworkContext';
import { useAgentTrustRegistry, readOnChainAgent, type OnChainAgent } from '../../hooks/useAgentTrustRegistry';

const MAX_REASON = 500;

interface Props {
  did: string;
  /** The agent's IDA status: 'suspended' shows Reactivate, anything live shows Suspend. */
  status: string;
  /** The chain the agent is registered on, if any. */
  agentChain: ChainInfo | null | undefined;
  suspensionReason?: string;
  onChanged: () => void;
}

/**
 * Suspend or reactivate an agent. Like decommissioning it has two halves: if the
 * agent is registered on-chain and the connected wallet is its operator, the
 * AgentTrustRegistry status is changed first (MetaMask signs), then IDA is
 * updated with the transaction hash. IDA is always updated, so a suspension
 * takes effect immediately even when the chain step is not possible.
 */
export default function AgentSuspendControl({ did, status, agentChain, suspensionReason, onChanged }: Props) {
  const { t } = useTranslation(['agents', 'common']);
  const { account, connect, provider } = useWeb3();
  const { selected } = useNetwork();
  const { setAgentStatus, isReady: walletReady } = useAgentTrustRegistry();
  const chainReady = walletReady && (!agentChain || agentChain.chainId === selected?.chainId);

  const suspending = status !== 'suspended';
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [chainState, setChainState] = useState<'checking' | 'unreadable' | OnChainAgent | null>('checking');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [chainTx, setChainTx] = useState('');

  if (status === 'decommissioned') return null;

  const openDialog = async () => {
    setOpen(true);
    setError('');
    setReason('');
    setChainTx('');
    setChainState('checking');
    try {
      setChainState(await readOnChainAgent(did, agentChain));
    } catch {
      setChainState('unreadable');
    }
  };

  const target = suspending ? 'suspended' : 'active';
  const onChainRecord = chainState !== null && typeof chainState === 'object' ? chainState : null;
  // The chain only needs changing when the agent is registered there and not
  // already in the target state (or retired).
  const chainNeedsChange =
    !!onChainRecord && onChainRecord.status !== 'decommissioned' && onChainRecord.status !== target;
  const walletIsOperator =
    chainNeedsChange && !!account && account.toLowerCase() === onChainRecord!.operator.toLowerCase();
  const canSignOnChain = chainNeedsChange && walletIsOperator && chainReady;
  const trimmed = reason.trim();
  const reasonValid = !suspending || (trimmed.length > 0 && trimmed.length <= MAX_REASON);

  const run = async (onChain: boolean) => {
    setBusy(true);
    setError('');
    let tx = chainTx;
    try {
      if (onChain && !tx) {
        tx = (await setAgentStatus(did, target)).txHash;
        setChainTx(tx);
      }
      const path = `/agents/${encodePathSegment(did)}/${suspending ? 'suspend' : 'reactivate'}`;
      const body = suspending ? { reason: trimmed, ...(tx ? { txHash: tx } : {}) } : tx ? { txHash: tx } : {};
      await apiFetch(path, { method: 'POST', body: JSON.stringify(body) });
      setOpen(false);
      setChainTx('');
      onChanged();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(
        tx
          ? `Updated on-chain (tx ${tx.slice(0, 10)}…), but updating IDA failed: ${msg}. Retry to finish.`
          : await describeTxError(e, { provider, account, chain: agentChain }),
      );
    } finally {
      setBusy(false);
    }
  };

  const label = suspending ? t('detail.suspend') : t('detail.reactivate');

  return (
    <>
      <button
        onClick={() => void openDialog()}
        className={
          suspending
            ? 'rounded-lg border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50'
            : 'rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50'
        }
      >
        <span className="material-symbols-outlined mr-1" style={{ fontSize: 14 }}>
          {suspending ? 'pause_circle' : 'play_circle'}
        </span>
        {label}
      </button>

      <Modal open={open} onClose={() => { if (!busy) setOpen(false); }} title={label}>
        <div className="space-y-4 text-sm">
          {suspending ? (
            <p className="text-text-main">
              Suspending pauses the agent until you reactivate it. While suspended it cannot mint or use capability
              tokens, issue or receive delegations, or take part in delegation chains. Nothing is revoked, so
              reactivating restores it as it was.
            </p>
          ) : (
            <p className="text-text-main">
              Reactivating lifts the suspension so the agent can act again.
              {suspensionReason ? ` It was suspended for: ${suspensionReason}` : ''}
            </p>
          )}

          {suspending && (
            <label className="block">
              <span className="text-xs font-semibold text-text-muted">{t('detail.suspensionReason')}</span>
              <textarea
                id="suspension-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={MAX_REASON}
                rows={3}
                placeholder="For example: suspected key leak, under investigation"
                className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
              <span className="text-[11px] text-text-faint">
                Required. Recorded in the audit trail. {trimmed.length}/{MAX_REASON}
              </span>
            </label>
          )}

          {chainState === 'checking' && (
            <p className="flex items-center gap-2 text-text-faint">
              <span className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Checking the agent's on-chain record…
            </p>
          )}
          {chainState === 'unreadable' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
              The blockchain could not be read, so it is unknown whether this agent is registered on-chain. IDA is
              updated either way; check the chain later.
            </div>
          )}
          {chainState === null && (
            <p className="text-text-faint">This agent is not registered on-chain, so only the IDA record changes.</p>
          )}
          {onChainRecord && !chainNeedsChange && (
            <p className="text-text-faint">The on-chain record needs no change. Only the IDA record changes.</p>
          )}
          {canSignOnChain && (
            <p className="text-text-main">MetaMask will ask you to sign the status change, then IDA is updated.</p>
          )}
          {chainNeedsChange && !canSignOnChain && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
              <p className="font-semibold">This agent is registered on-chain.</p>
              <p className="mt-1">
                Only the wallet that registered it (<span className="font-mono break-all">{onChainRecord!.operator}</span>)
                can change its on-chain status.{' '}
                {!account
                  ? 'Connect that wallet to update the chain too.'
                  : walletIsOperator
                    ? `Select ${agentChain?.name ?? "the agent's network"} in the header and MetaMask to continue.`
                    : 'The connected wallet is a different one.'}{' '}
                If you continue without it, IDA is updated but the on-chain record keeps its current status.
              </p>
            </div>
          )}

          {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}

          <div className="flex flex-wrap justify-end gap-3">
            <button
              onClick={() => setOpen(false)}
              disabled={busy}
              className="rounded-lg border border-border-subtle px-4 py-2 font-medium text-text-main hover:bg-slate-50 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            {chainNeedsChange && !account && (
              <button onClick={() => void connect()} disabled={busy} className="btn-secondary">
                Connect wallet
              </button>
            )}
            {canSignOnChain ? (
              <button
                onClick={() => void run(true)}
                disabled={busy || !reasonValid}
                className="rounded-lg bg-amber-600 px-4 py-2 font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {busy ? 'Working…' : chainTx ? 'Retry updating IDA' : `${label} on-chain and in IDA`}
              </button>
            ) : (
              <button
                onClick={() => void run(false)}
                disabled={busy || !reasonValid || chainState === 'checking'}
                className="rounded-lg bg-amber-600 px-4 py-2 font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {busy ? 'Working…' : chainNeedsChange ? `${label} in IDA only` : label}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
